import type { ReactNode } from "react";

/**
 * "Território, documento e rastreabilidade": the product's own art family.
 * Flat SVG on paper: deep-green hairlines, solid shapes, one small lime area
 * at most. Three recurring motifs: the SH/CEST tile, the document with a
 * cited line and the UF territory. No gradients, masks or photos; colour
 * comes from tokens through the `.art-*` classes in styles/art.css.
 *
 * Every art is decorative (`aria-hidden`): the text beside it carries the
 * meaning, so no state is read from the drawing alone.
 */
export type StateArtKind =
  /** Nothing exists yet: a fiscal sheet, its annex line and a link to a UF. */
  | "first-use"
  /** Filters hide everything: a list with empty rows under a magnifier. */
  | "no-results"
  /** The inbox is clear: a tidy tray with a discreet check. */
  | "nothing-pending"
  /** A dependency blocks the operation: a sheet behind a closed barrier. */
  | "blocked"
  /** IOB agent waiting: an empty query window beside the agenda. */
  | "iob-waiting"
  /** IOB agent blocked (paused, credentials, failure): window behind a barrier. */
  | "iob-blocked"
  /** The viewer lost its stream; the agent itself is not implicated. */
  | "viewer-disconnected";

function Svg({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <svg
      className={className ? `state-art ${className}` : "state-art"}
      viewBox="0 0 120 80"
      width="120"
      height="80"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** A fiscal sheet with ruled lines; `cite` highlights one line in lime. */
function Sheet({
  x,
  y,
  w = 40,
  h = 52,
  cite,
  lines = 4,
}: {
  x: number;
  y: number;
  w?: number;
  h?: number;
  cite?: number;
  lines?: number;
}) {
  const fold = 9;
  return (
    <g>
      <path
        className="art-paper art-ink"
        d={`M${x} ${y}h${w - fold}l${fold} ${fold}v${h - fold}h-${w}z`}
      />
      <path className="art-ink" d={`M${x + w - fold} ${y}v${fold}h${fold}`} fill="none" />
      {Array.from({ length: lines }, (_, index) => {
        const lineY = y + 16 + index * 8;
        const width = index === lines - 1 ? w * 0.45 : w - 14;
        return index === cite ? (
          <rect
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed decorative lines.
            key={index}
            className="art-lime"
            x={x + 6}
            y={lineY - 3}
            width={w - 12}
            height={6}
            rx={1.5}
          />
        ) : (
          <path
            // biome-ignore lint/suspicious/noArrayIndexKey: fixed decorative lines.
            key={index}
            className="art-hair"
            d={`M${x + 7} ${lineY}h${width}`}
          />
        );
      })}
    </g>
  );
}

/** A simplified UF silhouette (not a real border: a civic territory mark). */
function Territory({ x, y, scale = 1 }: { x: number; y: number; scale?: number }) {
  return (
    <path
      className="art-tint art-ink"
      transform={`translate(${x} ${y}) scale(${scale})`}
      d="M5 6l8-5 7 3 6-1 3 7-2 6 4 5-5 7-8 1-5 3-6-4 0-6-5-5 2-6z"
    />
  );
}

/** The SH/CEST identity tile. */
function Tile({ x, y, s = 18, label }: { x: number; y: number; s?: number; label: string }) {
  return (
    <g>
      <rect className="art-paper art-ink" x={x} y={y} width={s} height={s} rx={4} />
      <text className="art-code" x={x + s / 2} y={y + s / 2 + 3} textAnchor="middle">
        {label}
      </text>
    </g>
  );
}

/** A small browser/query window. */
function Window({
  x,
  y,
  w = 62,
  h = 46,
  children,
}: {
  x: number;
  y: number;
  w?: number;
  h?: number;
  children?: ReactNode;
}) {
  return (
    <g>
      <rect className="art-paper art-ink" x={x} y={y} width={w} height={h} rx={5} />
      <path className="art-hair" d={`M${x} ${y + 10}h${w}`} />
      <circle className="art-dot" cx={x + 6} cy={y + 5} r={1.4} />
      <circle className="art-dot" cx={x + 11} cy={y + 5} r={1.4} />
      <circle className="art-dot" cx={x + 16} cy={y + 5} r={1.4} />
      {children}
    </g>
  );
}

function Barrier({ x, y, w }: { x: number; y: number; w: number }) {
  const stripes = Math.floor(w / 8);
  return (
    <g>
      <rect className="art-paper art-ink" x={x} y={y} width={w} height={8} rx={2} />
      {Array.from({ length: stripes }, (_, index) => (
        <path
          // biome-ignore lint/suspicious/noArrayIndexKey: fixed decorative stripes.
          key={index}
          className="art-caution-fill"
          d={`M${x + 3 + index * 8} ${y + 8}l4 -8h3l-4 8z`}
        />
      ))}
      <path className="art-ink" d={`M${x + 4} ${y + 8}v10M${x + w - 4} ${y + 8}v10`} />
    </g>
  );
}

export function StateArt({ kind, className }: { kind: StateArtKind; className?: string }) {
  switch (kind) {
    case "first-use":
      return (
        <Svg className={className}>
          <path className="art-ground" d="M8 72h104" />
          <Sheet x={16} y={12} cite={1} />
          <path className="art-link" d="M58 32c10 0 14 8 24 8" />
          <circle className="art-lime art-ink" cx={58} cy={32} r={2.4} />
          <Territory x={80} y={26} scale={1.05} />
          <circle className="art-ink-fill" cx={95} cy={42} r={2.2} />
        </Svg>
      );
    case "no-results":
      return (
        <Svg className={className}>
          <path className="art-ground" d="M8 72h104" />
          <rect className="art-paper art-ink" x={14} y={10} width={66} height={56} rx={6} />
          {[22, 34, 46, 58].map((rowY, index) => (
            <g key={rowY}>
              <rect
                className={index === 0 ? "art-tint" : "art-empty-row"}
                x={22}
                y={rowY - 4}
                width={50}
                height={8}
                rx={2}
              />
            </g>
          ))}
          <circle className="art-paper art-ink art-thick" cx={86} cy={44} r={13} />
          <path className="art-ink art-thick" d="M95.5 53.5l10 10" />
          <path className="art-hair" d="M80 44h12" />
        </Svg>
      );
    case "nothing-pending":
      return (
        <Svg className={className}>
          <path className="art-ground" d="M8 72h104" />
          <rect className="art-paper art-ink" x={32} y={24} width={48} height={24} rx={2} />
          <rect className="art-paper art-ink" x={28} y={30} width={56} height={20} rx={2} />
          <path className="art-hair" d="M36 38h26" />
          <path className="art-tint art-ink" d="M18 44h84l-6 22H24z" />
          <path className="art-hair" d="M34 54h52" />
          <circle className="art-lime art-ink" cx={92} cy={22} r={9} />
          <path className="art-ink art-thick" d="M88 22l3 3 5-6" fill="none" />
        </Svg>
      );
    case "blocked":
      return (
        <Svg className={className}>
          <path className="art-ground" d="M8 72h104" />
          <Sheet x={40} y={10} />
          <Barrier x={20} y={46} w={80} />
        </Svg>
      );
    case "iob-waiting":
      return (
        <Svg className={className}>
          <path className="art-ground" d="M6 72h108" />
          <Window x={8} y={14}>
            <rect className="art-empty-row" x={16} y={32} width={30} height={7} rx={2} />
            <rect className="art-empty-row" x={50} y={32} width={12} height={7} rx={2} />
            <path className="art-hair" d="M16 48h24" />
          </Window>
          <rect className="art-paper art-ink" x={78} y={14} width={34} height={46} rx={5} />
          <path className="art-hair" d="M78 24h34" />
          {[0, 1, 2].map((row) =>
            [0, 1, 2].map((column) => (
              <rect
                key={`${row}-${column}`}
                className={row === 1 && column === 2 ? "art-lime art-ink" : "art-empty-row"}
                x={83 + column * 9}
                y={29 + row * 9}
                width={6}
                height={6}
                rx={1.5}
              />
            )),
          )}
        </Svg>
      );
    case "iob-blocked":
      return (
        <Svg className={className}>
          <path className="art-ground" d="M6 72h108" />
          <Window x={26} y={10} w={68} h={44}>
            <rect className="art-empty-row" x={34} y={28} width={36} height={7} rx={2} />
          </Window>
          <Barrier x={14} y={46} w={92} />
        </Svg>
      );
    case "viewer-disconnected":
      return (
        <Svg className={className}>
          <path className="art-ground" d="M6 72h108" />
          <rect className="art-paper art-ink" x={10} y={16} width={44} height={32} rx={4} />
          <path className="art-ink" d="M26 48v8h12v-8M22 56h20" fill="none" />
          <path className="art-hair" d="M18 26h28M18 34h18" />
          <path className="art-ink" d="M54 32h7" fill="none" />
          <rect className="art-paper art-ink" x={61} y={28} width={6} height={8} rx={1.5} />
          <path className="art-ink" d="M67 30h3M67 34h3" fill="none" />
          <rect className="art-paper art-ink" x={77} y={28} width={6} height={8} rx={1.5} />
          <path className="art-ink" d="M83 32h5" fill="none" />
          <path className="art-caution-stroke" d="M72 25l1.5 3M74 32h2.5M72 39l1.5-3" />
          <Tile x={88} y={23} label="IOB" />
        </Svg>
      );
  }
}

/** Research flow glyphs: locate → fetch → extract → review. */
export type FlowGlyphKind =
  | "locate"
  | "fetch"
  | "extract"
  | "verify"
  | "review"
  | "query"
  | "capture"
  | "finding"
  | "source"
  | "evidence";

export function FlowGlyph({ kind }: { kind: FlowGlyphKind }) {
  return (
    <svg
      className="flow-glyph"
      viewBox="0 0 32 32"
      width="32"
      height="32"
      aria-hidden="true"
      focusable="false"
    >
      {kind === "locate" && (
        <>
          <circle className="art-paper art-ink" cx={14} cy={14} r={8} />
          <path className="art-ink art-thick" d="M20 20l6 6" />
          <path className="art-hair" d="M10 14h8M14 10v8" />
        </>
      )}
      {kind === "fetch" && (
        <>
          <path className="art-paper art-ink" d="M8 5h11l5 5v17H8z" />
          <path className="art-ink art-thick" d="M16 12v9m-4-4l4 4 4-4" fill="none" />
        </>
      )}
      {kind === "extract" && (
        <>
          <path className="art-paper art-ink" d="M6 5h11l5 5v17H6z" />
          <rect className="art-lime" x={9} y={15} width={10} height={4} rx={1} />
          <path className="art-hair" d="M9 11h7M9 23h8" />
          <rect className="art-paper art-ink" x={19} y={17} width={9} height={9} rx={2} />
        </>
      )}
      {kind === "verify" && (
        <>
          <rect className="art-paper art-ink" x={4} y={10} width={10} height={12} rx={2} />
          <rect className="art-paper art-ink" x={18} y={10} width={10} height={12} rx={2} />
          <path className="art-ink" d="M14 16h4" />
        </>
      )}
      {kind === "review" && (
        <>
          <circle className="art-paper art-ink" cx={16} cy={16} r={10} />
          <path className="art-ink art-thick" d="M11 16l3.5 3.5L21 13" fill="none" />
        </>
      )}
      {kind === "query" && (
        <>
          <rect className="art-paper art-ink" x={4} y={7} width={24} height={18} rx={3} />
          <path className="art-hair" d="M4 12h24" />
          <rect className="art-empty-row" x={8} y={16} width={12} height={5} rx={1} />
          <rect className="art-ink-fill" x={21} y={16} width={4} height={5} rx={1} />
        </>
      )}
      {kind === "capture" && (
        <>
          <path
            className="art-ink art-thick"
            d="M5 11V6h5M22 6h5v5M27 21v5h-5M10 26H5v-5"
            fill="none"
          />
          <rect className="art-tint" x={10} y={11} width={12} height={10} rx={1.5} />
        </>
      )}
      {kind === "finding" && (
        <>
          <rect className="art-paper art-ink" x={6} y={6} width={20} height={20} rx={5} />
          <text className="art-code" x={16} y={19} textAnchor="middle">
            ST
          </text>
        </>
      )}
      {kind === "source" && (
        <>
          <path className="art-tint art-ink" d="M5 12l11-6 11 6z" />
          <path className="art-ink" d="M8 13v10M13 13v10M19 13v10M24 13v10" />
          <path className="art-ink art-thick" d="M5 25h22" />
        </>
      )}
      {kind === "evidence" && (
        <>
          <path className="art-paper art-ink" d="M8 4h11l5 5v19H8z" />
          <path className="art-hair" d="M11 11h8M11 22h7" />
          <rect className="art-lime" x={10.5} y={14.5} width={11} height={4} rx={1} />
        </>
      )}
    </svg>
  );
}
